import { Variables } from 'graphql-request';
import { getConfig } from '../graphql';
import { getGraphqlRequestClient } from '../graphql/clients';
import { useAuth0 } from '../provider';
import { ErrorTypes } from '../provider/types';

/**
 * Factory that creates a useFetcher hook bound to a specific GraphQL URL.
 * Each app calls this once with its URL, then uses the returned hook in components.
 *
 * Usage:
 *   export const useFetcher = createUseFetcher(import.meta.env.VITE_GRAPHQL_URL);
 *   // or
 *   export const useFetcher = createUseFetcher(() => getConfig().url);
 */
export const createUseFetcher = (url: string | (() => string)) => {
  return <TData, TVariables extends Variables | undefined>(
    query: string,
    options?: RequestInit['headers'],
  ): ((variables?: TVariables) => Promise<TData>) => {
    const { getAccessTokenSilently } = useAuth0();
    const resolvedUrl = typeof url === 'function' ? url() : url;

    const client = getGraphqlRequestClient(resolvedUrl, {
      headers: options,
    });

    return async (variables?: TVariables) => {
      const credentials = await getAccessTokenSilently();

      if (credentials && 'status' in credentials && credentials.status === ErrorTypes.TOKEN_ERROR) {
        return Promise.reject(credentials);
      }

      if (credentials && 'accessToken' in credentials) {
        client.setHeader('Authorization', `Bearer ${credentials?.accessToken}`);
      }

      return client.request(query, variables);
    };
  };
};

// Internal useFetcher for passwordless-authenticator's own use.
// Does NOT reject on undefined credentials because it's used during the auth flow
// (ContactFields) before the user has authenticated.
export const useFetcher = <TData, TVariables extends Variables | undefined>(
  query: string,
  options?: RequestInit['headers'],
): ((variables?: TVariables) => Promise<TData>) => {
  const { getAccessTokenSilently } = useAuth0();

  const client = getGraphqlRequestClient(getConfig().url, {
    headers: options,
  });

  return async (variables?: TVariables) => {
    const credentials = await getAccessTokenSilently();

    if (credentials && 'accessToken' in credentials) {
      client.setHeader('Authorization', `Bearer ${credentials?.accessToken}`);
    }

    if (credentials && 'status' in credentials && credentials.status === ErrorTypes.TOKEN_ERROR) {
      return Promise.reject(credentials);
    }

    return client.request(query, variables);
  };
};
